import { useTranslation } from "react-i18next";
import { useLoggedInAs } from "shared/hooks";
import { getOrderUrl } from "shared/utils";
import { useStorefrontView } from "apps/storefront/StorefrontViewContext";
import {
  ITimelineItem,
  TimelineFilter,
} from "../../views/Timeline/useTimeline";
import { Circle, Flex } from "styled-system/jsx";
import { timelineGroupItemRecipe } from "./timelineGroupItem.recipe";
import { Button, Spinner, Tag, Text } from "@ttc3k/trekker";
import { ArrowRight } from "iconoir-react";
import { NoteCard } from "shared/components/molecules";

type TimelineGroupItemProps = {
  timelineItem: ITimelineItem;
  onOpenLeadModal: (leadId: string) => void;
  onOpenBroadcastModal: (broadcastId: string, timelineItemId: string) => void;
  onOpenFormResponseModal: (surveyResponseId: string) => void;
  onOpenEventModal: (eventId: string) => void;
  loadingTimelineItemId: string;
};

export const TimelineGroupItem = ({
  timelineItem,
  onOpenLeadModal,
  onOpenBroadcastModal,
  onOpenFormResponseModal,
  onOpenEventModal,
  loadingTimelineItemId,
}: TimelineGroupItemProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_DETAILS_MODAL.TIMELINE",
  });
  const loggedInAs = useLoggedInAs();
  const { storefrontId: contextStorefrontId } = useStorefrontView();

  if (timelineItem.filterGroup === TimelineFilter.NOTES) {
    return <NoteCard initialMode="view" note={timelineItem.note!} />;
  }

  if (timelineItem.filterGroup === TimelineFilter.MEMBERSHIP) {
    const membershipLabel = t(
      `MEMBERSHIP_KINDS.${timelineItem.membershipKind}`,
      { name: timelineItem.title },
    );
    const membershipStyles = timelineGroupItemRecipe({ visual: "purchase" });
    return (
      <Flex className={membershipStyles.root}>
        <Flex justifyContent="space-between" alignItems="center" gap="300">
          <Flex alignItems="center" gap="100">
            <Circle className={membershipStyles.circle} />
            <Text visual="smallSemiBold" className={membershipStyles.tag}>
              {t("ITEM_TAGS.MEMBERSHIP")}
            </Text>
          </Flex>
        </Flex>
        <Flex flexDir="column" gap="0" w="full">
          <Text
            visual="bodySemiBold"
            lineClamp={1}
            className={membershipStyles.title}
          >
            {membershipLabel}
            {timelineItem.membershipViaSuffix}
          </Text>
          {timelineItem.membershipActorName && (
            <Text className={membershipStyles.description}>
              {timelineItem.membershipActorName}
            </Text>
          )}
        </Flex>
      </Flex>
    );
  }

  const isNewInquiry =
    timelineItem.filterGroup === TimelineFilter.INQUIRIES &&
    timelineItem.status === "UNREAD";

  const { root, circle, tag, title, description, action } =
    timelineGroupItemRecipe({
      visual:
        timelineItem.filterGroup === TimelineFilter.PURCHASES
          ? "purchase"
          : timelineItem.filterGroup === TimelineFilter.INQUIRIES
            ? "inquiry"
            : timelineItem.filterGroup === TimelineFilter.BROADCASTS
              ? "broadcast"
              : "activity",
    });

  const handleClick = () => {
    if (timelineItem.filterGroup === TimelineFilter.PURCHASES) {
      window.open(
        getOrderUrl(timelineItem._id, loggedInAs, contextStorefrontId),
        "_blank",
        "noopener,noreferrer",
      );
    } else if (timelineItem.filterGroup === TimelineFilter.INQUIRIES) {
      if (timelineItem.surveyResponseId) {
        onOpenFormResponseModal(timelineItem.surveyResponseId);
      } else {
        onOpenLeadModal(timelineItem._id);
      }
    } else if (timelineItem.filterGroup === TimelineFilter.BROADCASTS) {
      if (timelineItem.surveyResponseId) {
        onOpenFormResponseModal(timelineItem.surveyResponseId);
      } else {
        onOpenBroadcastModal(timelineItem.broadcastId ?? "", timelineItem._id);
      }
    } else if (timelineItem.filterGroup === TimelineFilter.ACTIVITY) {
      onOpenEventModal(timelineItem.eventId ?? "");
    }
  };

  return (
    <Flex className={root}>
      {/* Tag and action button */}
      <Flex justifyContent="space-between" alignItems="center" gap="300">
        <Flex alignItems="center" gap="100">
          <Circle className={circle} />
          <Text visual="smallSemiBold" className={tag}>
            {timelineItem.filterGroup === TimelineFilter.INQUIRIES &&
            timelineItem.leadType === "SURVEY_RESPONSE"
              ? t("ITEM_TAGS.SURVEY_RESPONSE")
              : t(`ITEM_TAGS.${timelineItem.tag.toUpperCase()}`)}
          </Text>
          {isNewInquiry && (
            <Tag visual="accent.magenta" mode="light" size="sm">
              {t("ITEM_TAGS.NEW")}
            </Tag>
          )}
        </Flex>
        {([
          TimelineFilter.INQUIRIES,
          TimelineFilter.PURCHASES,
          TimelineFilter.BROADCASTS,
        ].includes(timelineItem.filterGroup) ||
          (timelineItem.filterGroup === TimelineFilter.ACTIVITY &&
            timelineItem.eventId)) && (
          <>
            {loadingTimelineItemId === timelineItem._id ? (
              <Spinner width={"16px"} height={"16px"} />
            ) : (
              <Button
                visual="unstyled"
                Icon={ArrowRight}
                size="sm"
                p="0"
                color={action}
                className={action}
                onClick={handleClick}
              />
            )}
          </>
        )}
      </Flex>

      <Flex flexDir="column" gap="0" w="full">
        {(timelineItem.title ||
          timelineItem.operator ||
          timelineItem.initiatedBy) && (
          <Text visual="bodySemiBold" lineClamp={1} className={title}>
            {timelineItem.title || timelineItem.operator || timelineItem.initiatedBy}
          </Text>
        )}

        {timelineItem.description && (
          <Text className={description}>{timelineItem.description}</Text>
        )}
      </Flex>
    </Flex>
  );
};
